import React, { createContext, useContext, useState, useEffect } from 'react';

interface ThemePaletteConfig {
  label: string;
  accentPrimary: string;
  accentSecondary: string;
  accentTertiary: string;
  accentNeutral: string;
  accentGradient: string;
  glow: string;
  surface: string;
}

const PALETTES: Record<string, ThemePaletteConfig> = {
  emerald: {
    label: 'Emerald Energy',
    accentPrimary: '#10b981',
    accentSecondary: '#3b82f6',
    accentTertiary: '#34d399',
    accentNeutral: '#0f172a',
    accentGradient: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #2563eb 100%)',
    glow: '0 0 25px rgba(16, 185, 129, 0.45)',
    surface: 'rgba(16, 185, 129, 0.12)'
  },
  nebula: {
    label: 'Nebula Nights',
    accentPrimary: '#8b5cf6',
    accentSecondary: '#22d3ee',
    accentTertiary: '#f472b6',
    accentNeutral: '#1f2937',
    accentGradient: 'linear-gradient(135deg, #8b5cf6 0%, #22d3ee 50%, #f472b6 100%)',
    glow: '0 0 25px rgba(139, 92, 246, 0.45)',
    surface: 'rgba(139, 92, 246, 0.12)'
  },
  aurora: {
    label: 'Aurora Burst',
    accentPrimary: '#f97316',
    accentSecondary: '#ec4899',
    accentTertiary: '#10b981',
    accentNeutral: '#1c1917',
    accentGradient: 'linear-gradient(135deg, #f97316 0%, #ec4899 55%, #10b981 100%)',
    glow: '0 0 25px rgba(249, 115, 22, 0.45)',
    surface: 'rgba(249, 115, 22, 0.15)'
  },
  cyber: {
    label: 'Cyber Wave',
    accentPrimary: '#22d3ee',
    accentSecondary: '#0ea5e9',
    accentTertiary: '#9333ea',
    accentNeutral: '#111827',
    accentGradient: 'linear-gradient(135deg, #22d3ee 0%, #0ea5e9 50%, #9333ea 100%)',
    glow: '0 0 25px rgba(14, 165, 233, 0.45)',
    surface: 'rgba(14, 165, 233, 0.12)'
  }
};

type ThemePaletteKey = keyof typeof PALETTES;

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  theme: 'dark' | 'light';
  themePalette: ThemePaletteKey;
  setThemePalette: (palette: ThemePaletteKey) => void;
  palettes: { key: ThemePaletteKey; label: string; gradient: string }[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage first, then system preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Default to dark mode (current design)
    return true;
  });

  const [themePalette, setThemePaletteState] = useState<ThemePaletteKey>(() => {
    const savedPalette = localStorage.getItem('themePalette');
    if (savedPalette && savedPalette in PALETTES) {
      return savedPalette as ThemePaletteKey;
    }
    return 'emerald';
  });

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const theme = isDarkMode ? 'dark' : 'light';

  useEffect(() => {
    // Save theme preference
    localStorage.setItem('theme', theme);

    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);

    // Update CSS custom properties
    const root = document.documentElement;

    if (isDarkMode) {
      // Dark theme colors
      root.style.setProperty('--bg-primary', 'rgba(15, 23, 42, 0.95)');
      root.style.setProperty('--bg-secondary', 'rgba(30, 41, 59, 0.8)');
      root.style.setProperty('--bg-tertiary', 'rgba(51, 65, 85, 0.6)');
      root.style.setProperty('--text-primary', '#e2e8f0');
      root.style.setProperty('--text-secondary', 'rgba(148, 163, 184, 0.8)');
      root.style.setProperty('--text-muted', 'rgba(148, 163, 184, 0.6)');
      root.style.setProperty('--border-primary', 'rgba(148, 163, 184, 0.1)');
      root.style.setProperty('--border-secondary', 'rgba(148, 163, 184, 0.2)');
      root.style.setProperty('--accent-primary', '#10b981');
      root.style.setProperty('--accent-secondary', '#3b82f6');
      root.style.setProperty('--sidebar-bg', 'rgba(15, 23, 42, 0.95)');
      root.style.setProperty('--topbar-bg', 'rgba(15, 23, 42, 0.95)');
      root.style.setProperty('--card-bg', 'rgba(30, 41, 59, 0.6)');
      root.style.setProperty('--input-bg', 'rgba(30, 41, 59, 0.8)');
      root.style.setProperty('--dropdown-bg', 'rgba(15, 23, 42, 0.95)');

      // PremiumStyles Mapping - Dark
      root.style.setProperty('--background-dark', '#0b1220');
      root.style.setProperty('--background-darker', '#0f172a');
      root.style.setProperty('--background-card', '#1e293b');
      root.style.setProperty('--border-accent', '#1f2937');
    } else {
      // Light theme colors
      root.style.setProperty('--bg-primary', 'rgba(255, 255, 255, 0.95)');
      root.style.setProperty('--bg-secondary', 'rgba(248, 250, 252, 0.8)');
      root.style.setProperty('--bg-tertiary', 'rgba(241, 245, 249, 0.6)');
      root.style.setProperty('--text-primary', '#1e293b');
      root.style.setProperty('--text-secondary', 'rgba(71, 85, 105, 0.8)');
      root.style.setProperty('--text-muted', 'rgba(100, 116, 139, 0.6)');
      root.style.setProperty('--border-primary', 'rgba(203, 213, 225, 0.4)');
      root.style.setProperty('--border-secondary', 'rgba(203, 213, 225, 0.6)');
      root.style.setProperty('--accent-primary', '#059669');
      root.style.setProperty('--accent-secondary', '#2563eb');
      root.style.setProperty('--sidebar-bg', 'rgba(255, 255, 255, 0.95)');
      root.style.setProperty('--topbar-bg', 'rgba(255, 255, 255, 0.95)');
      root.style.setProperty('--input-bg', 'rgba(241, 245, 249, 0.8)');
      root.style.setProperty('--dropdown-bg', 'rgba(255, 255, 255, 0.95)');

      // PremiumStyles Mapping - Light
      root.style.setProperty('--background-dark', '#f8fafc'); /* Light bg */
      root.style.setProperty('--background-darker', '#ffffff'); /* Lighter bg */
      root.style.setProperty('--background-card', '#ffffff');
      root.style.setProperty('--border-accent', '#e2e8f0');
    }
  }, [isDarkMode, theme]);

  useEffect(() => {
    const palette = PALETTES[themePalette];
    const root = document.documentElement;

    root.style.setProperty('--accent-primary', palette.accentPrimary);
    root.style.setProperty('--accent-secondary', palette.accentSecondary);
    root.style.setProperty('--accent-tertiary', palette.accentTertiary);
    root.style.setProperty('--accent-neutral', palette.accentNeutral);
    root.style.setProperty('--accent-gradient', palette.accentGradient);
    root.style.setProperty('--accent-glow', palette.glow);
    root.style.setProperty('--accent-surface', palette.surface);

    localStorage.setItem('themePalette', themePalette);
  }, [themePalette]);

  const setThemePalette = (palette: ThemePaletteKey) => {
    if (!PALETTES[palette]) return;
    setThemePaletteState(palette);
  };

  const palettes = (Object.entries(PALETTES) as [ThemePaletteKey, ThemePaletteConfig][]).map(([key, value]) => ({
    key,
    label: value.label,
    gradient: value.accentGradient
  }));

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode,
        toggleTheme,
        theme,
        themePalette,
        setThemePalette,
        palettes
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export type { ThemePaletteKey };
